import {
    forwardRef,
    useState,
    useRef,
    useCallback,
    useEffect,
    useId,
    useImperativeHandle,
    type CSSProperties,
    type KeyboardEvent,
    type ReactElement,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { TextInput } from "@primitives/TextInput";
import { Badge } from "@primitives/Badge";
import { motionStyle } from "@primitives/motionStyle";
import { Text } from "@primitives/Text";
import { SlidingPill } from "@primitives/SlidingPill";
import { Popover } from "@primitives/overlay";
import { color, space, radius, typography, useListItemMotion } from "@tokens/index";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** A single workflow search result passed in from the parent. */
export interface WorkflowSearchResult {
    readonly id: string;
    readonly entityType: string;
    readonly entityId: string;
    readonly entityDisplayName: string;
    readonly title: string;
    readonly kind: string;
    readonly createdAt: string;
}

export type SearchResultsPresentation = "popover" | "inline";

/** Imperative surface for the shell's `/` shortcut. */
export interface SearchBarHandle {
    /** Move focus into the input. */
    focus(): void;
}

export interface SearchBarProps {
    /** Called with the debounced query string. */
    readonly onSearch: (query: string) => void;
    /** Called when the user selects a result. */
    readonly onSelect: (result: WorkflowSearchResult) => void;
    /** Search results provided by the parent (experience-shell owns the fetch). */
    readonly results: readonly WorkflowSearchResult[];
    /** Total matching results on the server (for "X more" indicator). */
    readonly totalCount?: number;
    /** Whether a search request is currently in flight. */
    readonly isLoading?: boolean;
    /** Placeholder text for the input. */
    readonly placeholder?: string;
    /**
     * `popover` floats the results under the input. `inline` renders them in
     * the document flow, for use inside a search sheet.
     */
    readonly resultsPresentation?: SearchResultsPresentation;
    /** Focus the input on mount. */
    readonly autoFocus?: boolean;
}

// ---------------------------------------------------------------------------
// useDebounce – simple inline hook (no external dependency)
// ---------------------------------------------------------------------------

function useDebounce(value: string, delayMs: number): string {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(id);
    }, [value, delayMs]);

    return debounced;
}

// ---------------------------------------------------------------------------
// Badge variant mapping for entity types
// ---------------------------------------------------------------------------

type BadgeVariant = "default" | "success" | "warning" | "danger";

function entityBadgeVariant(entityType: string): BadgeVariant {
    switch (entityType.toLowerCase()) {
        case "customer":
            return "success";
        case "carrier":
            return "warning";
        case "order":
            return "default";
        case "load":
            return "danger";
        default:
            return "default";
    }
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = {
    wrapper: {
        position: "relative",
        width: "100%",
        fontFamily: typography.family.system,
    } satisfies CSSProperties,
    inlinePanel: {
        marginTop: space[2],
        background: color.bg.surface,
        border: `1px solid ${color.border.subtle}`,
        borderRadius: radius.lg,
        overflow: "hidden",
    } satisfies CSSProperties,
    list: {
        position: "relative",
        listStyle: "none",
        margin: 0,
        padding: space[1],
        overscrollBehavior: "contain",
    } satisfies CSSProperties,
    inlineList: {
        maxHeight: "50dvh",
        overflowY: "auto",
    } satisfies CSSProperties,
    item: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: space[3],
        minHeight: 48,
        padding: `${space[2]} ${space[3]}`,
        borderRadius: radius.md,
        cursor: "pointer",
        background: "transparent",
    } satisfies CSSProperties,
    itemBody: {
        position: "relative",
        zIndex: 1,
        flex: 1,
        minWidth: 0,
        display: "grid",
        gap: 2,
    } satisfies CSSProperties,
    metaLine: {
        display: "flex",
        alignItems: "center",
        gap: space[2],
        minWidth: 0,
        overflow: "hidden",
        whiteSpace: "nowrap",
    } satisfies CSSProperties,
    metaDot: {
        color: color.fg.subtle,
        flexShrink: 0,
    } satisfies CSSProperties,
    message: {
        padding: `${space[3]} ${space[4]}`,
        textAlign: "center",
    } satisfies CSSProperties,
} as const;

// ---------------------------------------------------------------------------
// Result row
// ---------------------------------------------------------------------------

function SearchResultItem({
    result,
    index,
    highlighted,
    onSelect,
    onHighlight,
}: {
    readonly result: WorkflowSearchResult;
    readonly index: number;
    readonly highlighted: boolean;
    readonly onSelect: (result: WorkflowSearchResult) => void;
    readonly onHighlight: (index: number) => void;
}): ReactElement {
    const arrival = useListItemMotion(index, false);
    return (
        <motion.li
            data-search-item
            data-pill-target
            id={`search-result-${result.id}`}
            role="option"
            aria-selected={highlighted}
            style={motionStyle(styles.item)}
            initial={arrival.initial}
            animate={arrival.animate}
            transition={arrival.transition}
            onClick={() => onSelect(result)}
            onMouseEnter={() => onHighlight(index)}
        >
            <div style={{ position: "relative", zIndex: 1, flexShrink: 0 }}>
                <Badge variant={entityBadgeVariant(result.entityType)} size="sm">
                    {result.entityType}
                </Badge>
            </div>
            <div style={styles.itemBody}>
                <Text role="body" weight="semibold" tone="default" truncate>
                    {result.title}
                </Text>
                <div style={styles.metaLine}>
                    <Text role="caption" tone="muted" truncate>
                        {result.entityDisplayName}
                    </Text>
                    <span aria-hidden="true" style={styles.metaDot}>
                        ·
                    </span>
                    <Text role="mono" size="xs" tone="subtle" style={{ flexShrink: 0 }}>
                        {result.entityId}
                    </Text>
                    <span aria-hidden="true" style={styles.metaDot}>
                        ·
                    </span>
                    <Text role="caption" tone="subtle" style={{ flexShrink: 0 }}>
                        {result.kind}
                    </Text>
                </div>
            </div>
        </motion.li>
    );
}

// ---------------------------------------------------------------------------
// SearchBar component
// ---------------------------------------------------------------------------

/**
 * A domain-aware search bar composition for finding workflows.
 *
 * The parent in experience-shell owns the data fetch via `useSearchWorkflows`
 * and passes `results`, `isLoading`, and callbacks as props.
 *
 * The input is the `combobox`; the results panel is the `listbox`. Arrow
 * keys move the highlight, Enter selects the highlighted result — or the
 * first one when nothing is highlighted, so the happy path is type + Enter.
 * Escape closes.
 */
export const SearchBar = forwardRef<SearchBarHandle, SearchBarProps>(function SearchBar(
    {
        onSearch,
        onSelect,
        results,
        totalCount,
        isLoading = false,
        placeholder = "Search workflows…",
        resultsPresentation = "popover",
        autoFocus = false,
    },
    ref,
) {
    const [query, setQuery] = useState("");
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const [isOpen, setIsOpen] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listId = useId();

    useImperativeHandle(
        ref,
        () => ({
            focus: () => {
                inputRef.current?.focus();
                inputRef.current?.select();
            },
        }),
        [],
    );

    const debouncedQuery = useDebounce(query, 300);

    // Notify parent when debounced query changes
    const prevDebouncedRef = useRef(debouncedQuery);
    useEffect(() => {
        if (debouncedQuery !== prevDebouncedRef.current) {
            prevDebouncedRef.current = debouncedQuery;
            onSearch(debouncedQuery);
        }
    }, [debouncedQuery, onSearch]);

    // Open the results when we have results or are loading with a query
    useEffect(() => {
        if (query.length > 0 && (results.length > 0 || isLoading)) {
            setIsOpen(true);
        }
    }, [results, isLoading, query]);

    // Reset highlight when results change
    useEffect(() => {
        setHighlightIndex(-1);
    }, [results]);

    const close = useCallback(() => setIsOpen(false), []);

    const selectResult = useCallback(
        (result: WorkflowSearchResult) => {
            onSelect(result);
            setIsOpen(false);
            setQuery("");
        },
        [onSelect],
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (!isOpen || results.length === 0) {
                if (e.key === "Escape") {
                    setIsOpen(false);
                    inputRef.current?.blur();
                }
                return;
            }

            switch (e.key) {
                case "ArrowDown": {
                    e.preventDefault();
                    setHighlightIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
                    break;
                }
                case "ArrowUp": {
                    e.preventDefault();
                    setHighlightIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
                    break;
                }
                case "Enter": {
                    e.preventDefault();
                    const selected = results[highlightIndex >= 0 ? highlightIndex : 0];
                    if (selected) {
                        selectResult(selected);
                    }
                    break;
                }
                case "Escape": {
                    e.preventDefault();
                    setIsOpen(false);
                    inputRef.current?.blur();
                    break;
                }
            }
        },
        [isOpen, results, highlightIndex, selectResult],
    );

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightIndex >= 0 && listRef.current) {
            const items = listRef.current.querySelectorAll("[data-search-item]");
            items[highlightIndex]?.scrollIntoView({ block: "nearest" });
        }
    }, [highlightIndex]);

    const showResults = isOpen && query.length > 0 && (results.length > 0 || isLoading);
    const inline = resultsPresentation === "inline";

    const resultsBody =
        isLoading && results.length === 0 ? (
            <div style={styles.message}>
                <Text role="bodySm" tone="muted">
                    Searching…
                </Text>
            </div>
        ) : (
            <ul
                ref={listRef}
                role="presentation"
                style={inline ? { ...styles.list, ...styles.inlineList } : styles.list}
            >
                <SlidingPill
                    containerRef={listRef}
                    activeIndex={highlightIndex}
                    itemSelector="[data-pill-target]"
                    style={{ borderRadius: radius.md, background: color.bg.hover }}
                />
                {/* Results change on every keystroke. Rows present when the
                    list opens animate nothing; rows that arrive for a later
                    query fade in place, so typing never replays an entrance. */}
                <AnimatePresence initial={false}>
                    {results.map((result, i) => (
                        <SearchResultItem
                            key={result.id}
                            result={result}
                            index={i}
                            highlighted={highlightIndex === i}
                            onSelect={selectResult}
                            onHighlight={setHighlightIndex}
                        />
                    ))}
                </AnimatePresence>
                {totalCount !== undefined && totalCount > results.length && (
                    <li
                        role="presentation"
                        style={{ padding: `${space[2]} ${space[3]}`, textAlign: "center" }}
                    >
                        <Text role="caption" tone="subtle">
                            showing {String(results.length)} of {String(totalCount)}
                        </Text>
                    </li>
                )}
            </ul>
        );

    return (
        <div ref={wrapperRef} style={styles.wrapper} data-search-bar="">
            <TextInput
                ref={inputRef}
                value={query}
                autoFocus={autoFocus}
                onChange={(e) => {
                    setQuery(e.target.value);
                    if (e.target.value.length === 0) {
                        setIsOpen(false);
                    }
                }}
                onFocus={() => {
                    if (query.length > 0 && results.length > 0) {
                        setIsOpen(true);
                    }
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                fullWidth
                role="combobox"
                aria-expanded={showResults}
                aria-haspopup="listbox"
                aria-controls={showResults ? listId : undefined}
                aria-autocomplete="list"
                aria-activedescendant={
                    highlightIndex >= 0 ? `search-result-${results[highlightIndex]?.id}` : undefined
                }
            />

            {inline ? (
                showResults ? (
                    <div id={listId} role="listbox" style={styles.inlinePanel}>
                        {resultsBody}
                    </div>
                ) : null
            ) : (
                <Popover
                    id={listId}
                    open={showResults}
                    onClose={close}
                    anchorRef={wrapperRef}
                    placement="bottom-start"
                    matchAnchorWidth
                    role="listbox"
                    initialFocus="none"
                    returnFocus={false}
                >
                    {resultsBody}
                </Popover>
            )}
        </div>
    );
});
